import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useInstantSearch } from 'react-instantsearch';
import { defineMessages } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { SearchLayoutContainer } from '@sorare/core/src/components/search/SearchLayout/ui';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import {
  PlayerSearchViewAttributes,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';
import { useShowBottomBarNav } from '@sorare/core/src/routing/MultiSportBottomNavBar/useShowBottomBarNav';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import {
  BackendPaginatedSearchPlayerTable,
  PaginatedSearchPlayerTable,
} from 'components/searchPlayerTable/PaginatedSearchPlayerTable';
import { ActiveFilters } from 'search/ActiveFilters';
import {
  BackendFiltersManagerDialog,
  FiltersManagerDialog,
} from 'search/FiltersManagerDialog';
import { BackendSearchBox, SearchBox } from 'search/SearchBox';
import {
  BackendToggleFiltersManager,
  ToggleFiltersManager,
} from 'search/ToggleFiltersManager';
import { PlayerSearchHelp } from 'searchCommonPlayer/PlayerSearchHelp';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';
import { ToggleBaseballStatsMode } from 'searchCommonPlayer/ToggleBaseballStatsMode';
import { ToggleStatsManager } from 'searchCommonPlayer/ToggleStatsManager';

import { BackendSegment, Segment } from '../Segment';
import type { BackendPlayerSearchLayout_AnyPlayerInterface } from './__generated__/PlayerSearchLayout.graphql';
import type { Props } from './types';

const NoSmoothScrollStyle = createGlobalStyle`
  html {
    /* overrides the default scroll behavior we have in global style.css */
    scroll-behavior: initial;
  }
`;

const Root = styled(Vertical).attrs({ gap: 3 })`
  --search-extended-ctn-width: 240px;
`;
const SearchLayoutMain = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;

const Header = styled(Vertical)`
  padding: var(--unit) var(--double-unit);
  @media ${tabletAndAbove} {
    padding: var(--unit) var(--triple-unit);
  }
`;
const Filters = styled(Horizontal).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  overflow: auto;
  justify-content: space-between;
  flex-shrink: 0;
  width: 100%;
`;

const Sticky = styled(Horizontal)`
  @media ${laptopAndAbove} {
    flex-shrink: 0;
    position: sticky;
    z-index: 1;
  }
`;
const SearchLeftSticky = styled(Sticky)`
  @media ${laptopAndAbove} {
    padding-right: var(--unit);
    margin-right: calc(var(--unit) * -1);
    left: 0;
    background: var(--c-black);
  }
`;
const FilterLeftSticky = styled(Sticky)`
  @media ${laptopAndAbove} {
    padding-right: var(--unit);
    margin-right: calc(var(--unit) * -1);
    left: calc(var(--search-extended-ctn-width) + var(--unit));
    background: linear-gradient(
      90deg,
      rgba(var(--c-rgb-black), 1) 0%,
      rgba(var(--c-rgb-black), 1) calc(100% - var(--half-unit)),
      transparent 100%
    );
  }
`;

const RightSticky = styled(Sticky)`
  right: 0;
  padding-left: var(--unit);
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(var(--c-rgb-black), 1) var(--half-unit),
    rgba(var(--c-rgb-black), 1) 100%
  );
`;

const Separator = styled.div`
  width: 1px;
  height: 1em;
  background-color: var(--c-nd-200);
`;

const SearchBoxCtn = styled.label`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  transition: 0.2s;
  overflow: hidden;
  &:focus-within {
    width: var(--search-extended-ctn-width);
  }
  @media ${laptopAndAbove} {
    width: var(--search-extended-ctn-width);
  }
`;
const Grid = styled.div`
  @media ${tabletAndAbove} {
    padding: 0 var(--triple-unit);
  }
`;
const StyledContentWithStickyHeader = styled(ContentWithStickyHeader)`
  width: 100%;
`;
const StyledFullViewportScroll = styled(FullViewportScroll)`
  width: 100%;
`;
const ToggleStatsManagerWrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

const messages = defineMessages({
  defaultPlaceholder: {
    id: 'AdvancedCardSearch.scoutingCenter',
    defaultMessage: 'Filter by player or team',
  },
});

export const PlayerSearchLayout = (props: Props) => {
  const bottomNavBar = useShowBottomBarNav();
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const {
    topic,
    cardFilters,
    sorts,
    children,
    sport,
    watchlistId,
    canManageWatchlist,
    favoritesWatchlist,
    dropdownFilters,
  } = props;
  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const { refresh } = useInstantSearch();
  const { statsManager, sawOnboarding } = useManagePlayerSearchView();
  const shouldShowFiltersOnboarding =
    statsManager[PlayerSearchViewAttributes.ONBOARDING] ===
    StatsOnboarding.FILTERS;

  const searchBoxPlaceholder = topic
    ? {
        scoutingCenter: messages.defaultPlaceholder,
        watchlist: messages.defaultPlaceholder,
      }[topic.type]
    : messages.defaultPlaceholder;

  const searchLayout = (
    <Root>
      <NoSmoothScrollStyle />
      <SearchLayoutContainer>
        <StyledContentWithStickyHeader
          header={
            <Header>
              <ConditionalWrapper
                wrap={!isLaptopOrAbove}
                Wrapper={StyledFullViewportScroll}
              >
                <Filters>
                  <Horizontal>
                    <SearchLeftSticky>
                      <SearchBoxCtn>
                        <SearchBox
                          placeholder={formatMessage(searchBoxPlaceholder, {
                            name: topic?.label,
                          })}
                        />
                      </SearchBoxCtn>
                    </SearchLeftSticky>
                    <ToggleBaseballStatsMode />
                    <FilterLeftSticky>
                      <PlayerSearchHelp
                        step={StatsOnboarding.FILTERS}
                        placement="top"
                      >
                        <ToggleFiltersManager
                          color="quaternary"
                          toggleFilters={() => {
                            if (shouldShowFiltersOnboarding)
                              sawOnboarding(StatsOnboarding.FILTERS);
                            toggleFilters();
                          }}
                          size="small"
                        />
                      </PlayerSearchHelp>
                      <Separator />
                    </FilterLeftSticky>
                    <Segment
                      dropdownFilters={dropdownFilters}
                      hideFavoriteFilter={Boolean(
                        watchlistId || favoritesWatchlist
                      )}
                    />
                  </Horizontal>
                  {!!(isLaptopOrAbove || !bottomNavBar) && (
                    <RightSticky>
                      <ToggleStatsManager />
                    </RightSticky>
                  )}
                </Filters>
              </ConditionalWrapper>
              <ActiveFilters forceScroll />
            </Header>
          }
        >
          <SearchLayoutMain>
            {children}
            <Grid>
              <PaginatedSearchPlayerTable
                topic={topic}
                sport={sport}
                indexes={sorts}
                watchlistId={watchlistId}
                canManageWatchlist={canManageWatchlist}
                favoritesWatchlist={favoritesWatchlist}
              />
            </Grid>
          </SearchLayoutMain>
        </StyledContentWithStickyHeader>
      </SearchLayoutContainer>
      {!isLaptopOrAbove && !!bottomNavBar && (
        <Portal id="above-bottom-bar-portal">
          <ToggleStatsManagerWrapper>
            <ToggleStatsManager />
          </ToggleStatsManagerWrapper>
        </Portal>
      )}
      <FiltersManagerDialog
        open={filtersOpen}
        onClose={toggleFilters}
        filtersGroups={cardFilters}
      />
    </Root>
  );
  if (isLaptopOrAbove) return searchLayout;
  return (
    <PullToRefetch refetch={async () => refresh()}>
      {searchLayout}
    </PullToRefetch>
  );
};

export const BackendPlayerSearchLayout = (
  props: Omit<Props, 'GridComponent'> & {
    items: { anyPlayer: BackendPlayerSearchLayout_AnyPlayerInterface }[];
    loading: boolean;
    refetch: () => void;
  }
) => {
  const bottomNavBar = useShowBottomBarNav();
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const {
    topic,
    cardFilters,
    sorts,
    children,
    sport,
    watchlistId,
    favoritesWatchlist,
    dropdownFilters,
    items,
    loading,
    refetch: refresh,
  } = props;
  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const { statsManager, sawOnboarding } = useManagePlayerSearchView();
  const shouldShowFiltersOnboarding =
    statsManager[PlayerSearchViewAttributes.ONBOARDING] ===
    StatsOnboarding.FILTERS;

  const searchBoxPlaceholder = topic
    ? {
        scoutingCenter: messages.defaultPlaceholder,
        watchlist: messages.defaultPlaceholder,
      }[topic.type]
    : messages.defaultPlaceholder;

  const searchLayout = (
    <Root>
      <NoSmoothScrollStyle />
      <SearchLayoutContainer>
        <StyledContentWithStickyHeader
          header={
            <Header>
              <ConditionalWrapper
                wrap={!isLaptopOrAbove}
                Wrapper={StyledFullViewportScroll}
              >
                <Filters>
                  <Horizontal>
                    <SearchLeftSticky>
                      <SearchBoxCtn>
                        <BackendSearchBox
                          placeholder={formatMessage(searchBoxPlaceholder, {
                            name: topic?.label,
                          })}
                        />
                      </SearchBoxCtn>
                    </SearchLeftSticky>
                    {/* TODO */}
                    {/* <ToggleBaseballStatsMode /> */}
                    <FilterLeftSticky>
                      <PlayerSearchHelp
                        step={StatsOnboarding.FILTERS}
                        placement="top"
                      >
                        <BackendToggleFiltersManager
                          color="quaternary"
                          toggleFilters={() => {
                            if (shouldShowFiltersOnboarding)
                              sawOnboarding(StatsOnboarding.FILTERS);
                            toggleFilters();
                          }}
                          size="small"
                        />
                      </PlayerSearchHelp>
                      <Separator />
                    </FilterLeftSticky>
                    <BackendSegment
                      dropdownFilters={dropdownFilters}
                      hideFavoriteFilter={Boolean(
                        watchlistId || favoritesWatchlist
                      )}
                    />
                  </Horizontal>
                  {!!(isLaptopOrAbove || !bottomNavBar) && (
                    <RightSticky>
                      <ToggleStatsManager />
                    </RightSticky>
                  )}
                </Filters>
              </ConditionalWrapper>
              {/* <ActiveFilters forceScroll /> */}
            </Header>
          }
        >
          <SearchLayoutMain>
            {children}
            <Grid>
              <BackendPaginatedSearchPlayerTable
                items={items}
                loading={loading}
                topic={topic}
                sport={sport}
                indexes={sorts}
                watchlistId={watchlistId}
              />
            </Grid>
          </SearchLayoutMain>
        </StyledContentWithStickyHeader>
      </SearchLayoutContainer>
      {!isLaptopOrAbove && !!bottomNavBar && (
        <Portal id="above-bottom-bar-portal">
          <ToggleStatsManagerWrapper>
            <ToggleStatsManager />
          </ToggleStatsManagerWrapper>
        </Portal>
      )}
      <BackendFiltersManagerDialog
        open={filtersOpen}
        onClose={toggleFilters}
        filtersGroups={cardFilters}
      />
    </Root>
  );
  if (isLaptopOrAbove) return searchLayout;
  return (
    <PullToRefetch refetch={async () => refresh()}>
      {searchLayout}
    </PullToRefetch>
  );
};

BackendPlayerSearchLayout.fragments = {
  AnyPlayerInterface: gql`
    fragment BackendPlayerSearchLayout_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ...BackendPaginatedSearchPlayerTable_AnyPlayerInterface
    }
    ${BackendPaginatedSearchPlayerTable.fragments.AnyPlayerInterface}
  ` as TypedDocumentNode<BackendPlayerSearchLayout_AnyPlayerInterface>,
};
